import { Reveal } from "./Reveal";

const ChapterShell = ({ title, children, image, imageAlt, imageTestId, reverse = false, contain = false }) => (
  <div className={`grid lg:grid-cols-12 gap-8 lg:gap-16 items-center py-14 sm:py-20 lg:py-28 border-t border-[#A89DE3]/15`}>
    <div className={`lg:col-span-7 ${reverse ? "lg:order-2" : ""}`}>
      <Reveal>
        <h3 className="font-display font-extrabold tracking-tight leading-[1.02] text-[1.75rem] sm:text-4xl lg:text-5xl text-[#FFFFFF] whitespace-pre-line">
          {title}
        </h3>
      </Reveal>
      <Reveal delay={0.15} className="mt-5 sm:mt-7 text-[#D9D6EC] text-sm sm:text-base md:text-lg leading-relaxed space-y-4 sm:space-y-5">
        {children}
      </Reveal>
    </div>
    <div className={`lg:col-span-5 ${reverse ? "lg:order-1" : ""}`}>
      <Reveal delay={0.2}>
        <div className="overflow-hidden rounded-3xl border border-[#A89DE3]/20 shadow-2xl shadow-black/40">
          <img
            src={image}
            alt={imageAlt}
            data-testid={imageTestId}
            loading="lazy"
            decoding="async"
            className={`w-full ${contain ? "h-auto" : "h-[260px] sm:h-[460px] object-cover"} hover:scale-105 transition-transform duration-700`}
          />
        </div>
      </Reveal>
    </div>
  </div>
);

const PAIN_POINTS = [
  "You're doing the work. Your body isn't cooperating.",
  "You've tried everything - but still feel STUCK.",
  "You're already eating less and exercising, but the scale isn't moving.",
  "The only time you lose weight is when you're doing something extreme.",
  "Every time progress stops, you cut food or add cardio.",
  "Tired. Bloated. Foggy. Hungry. Fighting cravings.",
];

export default function Chapters() {
  return (
    <section id="manifesto" data-testid="manifesto-section" className="bg-[#0B0B12] px-4 sm:px-8 lg:px-12">
      <div className="max-w-7xl mx-auto pb-8">
        <Reveal className="pt-14 sm:pt-20 lg:pt-28 pb-4 sm:pb-6">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.25em] text-[#A89DE3]">The Manifesto</p>
          <h2 data-testid="before-diet-title" className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-[#FFFFFF] mt-4 leading-[1.02]">
            BEFORE YOU START<br />ANOTHER DIET&hellip;
          </h2>
        </Reveal>

        <ChapterShell
          title="DOES THIS SOUND LIKE YOU?"
          image="/images/transformation.jpg"
          imageAlt="Real before and after transformation"
          imageTestId="before-after-image"
          reverse
        >
          <ul data-testid="pain-points-section" className="space-y-3">
            {PAIN_POINTS.map((point, i) => (
              <li key={i} data-testid={`pain-point-item-${i}`} className="flex items-baseline gap-3 rounded-xl border border-[#A89DE3]/20 bg-[#4B2A9E]/40 px-4 py-3 hover:border-[#7C89FF]/60 transition-colors duration-300">
                <span className="font-display font-extrabold text-[#7C89FF] text-sm shrink-0">0{i + 1}</span>
                <span className="text-[#EDEBF8]">{point}</span>
              </li>
            ))}
          </ul>
          <p className="font-display text-lg sm:text-xl font-bold text-[#FFFFFF] leading-snug">
            You don't need to try harder. You need to get your body{" "}
            <span className="text-[#7C89FF] italic">RESPONDING</span> again.
          </p>
        </ChapterShell>

        <ChapterShell
          title={"2-10 LBS\nCOMMON SCALE-WEIGHT LOSS"}
          image="/images/energy.jpg"
          imageAlt="Strong, energised woman outdoors"
          imageTestId="more-energy-image"
        >
          <p data-testid="before-diet-weight-loss-stat">
            The women I take through this foundational metabolic reset commonly lose 2-10 pounds while we{" "}
            <span className="text-[#FFFFFF] font-semibold">increase food</span> and intentionally remove weight training.
          </p>
          <p>
            But I'm not only watching the scale. We're looking for changes in energy, digestion, bloating, sleep, hunger,
            cravings, bowel regularity and brain fog.
          </p>
          <p className="font-display font-bold text-[#7C89FF] tracking-wide">A HEALTHY BODY IS A RESPONSIVE BODY.</p>
        </ChapterShell>

        <ChapterShell
          title={"MORE ENERGY.\nLESS FOG."}
          image="/images/activate-banner.png"
          imageAlt="ACTIVATE your inner ENERGY"
          imageTestId="activate-energy-banner"
          reverse
          contain
        >
          <p>
            This is bigger than a number on the scale. Imagine eating{" "}
            <span className="text-[#FFFFFF] font-semibold">MORE</span> whole food while feeling less puffy, less exhausted
            and less obsessed with forcing your body to change.
          </p>
          <p>
            For 14 days we simplify everything: structured nourishment, foundational supplementation, hydration, walking,
            restorative yoga, sleep and recovery.
          </p>
          <p className="font-display font-bold text-[#FFFFFF]">No punishment. No starvation. No trying to burn off your food.</p>
        </ChapterShell>

        <ChapterShell
          title={"YES. WE'RE EATING MORE.\nLESS FOOD ISN'T AUTOMATICALLY BETTER."}
          image="/images/meals.jpg"
          imageAlt="Whole-food meal prep — chicken, asparagus, sweet potato and rice"
          imageTestId="eating-more-image"
        >
          <p>
            This isn't another crash diet disguised as a &ldquo;reset.&rdquo; We intentionally increase nourishment with
            structured whole foods while temporarily reducing training stress.
          </p>
          <p>
            Calories matter. Energy balance matters. But continually demanding MORE output while providing LESS input
            isn't the answer every time a woman gets stuck.
          </p>
          <p className="font-display font-bold text-[#7C89FF] tracking-wide">
            TRY THIS BEFORE YOU COMMIT TO ANOTHER CALORIE DEFICIT AND MORE CARDIO.
          </p>
        </ChapterShell>

        <ChapterShell
          title={"YES. I'M A BODY BUILDER\nAND I'M TELLING YOU NOT TO LIFT."}
          image="/images/bodybuilder.jpg"
          imageAlt="Crystal Stretch competing on stage as a bodybuilder"
          imageTestId="bodybuilder-image"
          reverse
        >
          <p className="font-display font-bold text-[#FFFFFF] text-lg">FOR 14 DAYS. ON PURPOSE.</p>
          <p>
            <span className="text-[#7C89FF] font-semibold">NO</span> weight training.{" "}
            <span className="text-[#7C89FF] font-semibold">NO</span> HIIT.{" "}
            <span className="text-[#7C89FF] font-semibold">NO</span> punishment cardio. Instead: walking, restorative
            yoga, whole foods, hydration, sleep, digestive support and recovery.
          </p>
          <p>
            Training isn't gone forever. We're strategically removing a physical stressor while increasing nourishment
            and prioritizing recovery.
          </p>
          <p className="font-display font-bold text-[#7C89FF] tracking-wide">
            THIS ISN'T ABOUT BURNING MORE. IT'S ABOUT GETTING YOUR BODY RESPONSIVE AGAIN.
          </p>
        </ChapterShell>
      </div>
    </section>
  );
}
